/* eslint no-underscore-dangle: 0 */
/* eslint no-nested-ternary: 0 */
import React, { useMemo, useState } from 'react';

// material-ui
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material';

// third-party
import { Link as RouterLink } from 'react-router-dom';

// project imports
import useCrmGQL from 'hooks/crm/useGQL';
import useGQL from 'hooks/useGQL';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { CrmTask } from 'types/crm';
import MainCard from 'ui-component/cards/MainCard';
import TaskDialog, { TaskDialogValues } from 'views/crm/tasks/TaskDialog';
import { dueCell } from 'views/crm/tasks/taskDue';

/**
 * Calendar Tasks panel (POGO-13295): a narrow checklist of the signed-in
 * admin's open tasks, shown to the right of the calendar grid.
 *
 * Phase 2 — the write path. Ticking a row's checkbox completes the task
 * (one-way); "Add task" opens the shared TaskDialog in create mode; clicking
 * a row opens the same dialog in edit mode where the admin can write/edit a
 * note (the dialog's Notes field). Every write follows the feature-wide
 * `await mutation → notify() → await refetch()` convention, reusing the
 * standalone Tasks page's handler shapes verbatim.
 */
const TasksPanel = () => {
    const dispatch = useDispatch();

    const { LIST_TASKS, CREATE_TASK, UPDATE_TASK, COMPLETE_TASK } = useCrmGQL();
    const { GET_ALL_ADMINS } = useGQL();

    const { data, loading, refetch } = LIST_TASKS({ scope: 'mine', status: 'open' });
    const { data: adminData } = GET_ALL_ADMINS();
    const [handleCreateTask, { loading: creating }] = CREATE_TASK();
    const [handleUpdateTask, { loading: updating }] = UPDATE_TASK();
    const [handleCompleteTask] = COMPLETE_TASK();

    /** null = list; 'new' = creating; otherwise the task being edited */
    const [editing, setEditing] = useState<CrmTask | 'new' | null>(null);

    const tasks: CrmTask[] = data?.listCrmTasks || [];
    const admins = useMemo(
        () =>
            (adminData?.getAdminList?.adminList || []).map((admin: { _id: string; name: string }) => ({
                _id: admin._id,
                name: admin.name
            })),
        [adminData]
    );

    const notify = (message: string, color: 'success' | 'error') => {
        dispatch(
            openSnackbar({
                open: true,
                message,
                anchorOrigin: { horizontal: 'center' },
                variant: 'alert',
                alert: { color }
            })
        );
    };

    const handleSave = async (values: TaskDialogValues) => {
        try {
            if (editing && editing !== 'new') {
                await handleUpdateTask({ variables: { input: { ...values, taskId: editing._id } } });
                notify(`Task '${values.title}' updated`, 'success');
            } else {
                await handleCreateTask({
                    variables: {
                        input: {
                            title: values.title,
                            description: values.description,
                            ownerId: values.ownerId,
                            accountId: values.accountId || undefined,
                            contactId: values.contactId || undefined,
                            dueAt: values.dueAt || undefined,
                            reminderAt: values.reminderAt || undefined,
                            isRecurring: values.isRecurring || undefined,
                            recurrencePattern: values.recurrencePattern || undefined
                        }
                    }
                });
                notify(`Task '${values.title}' created`, 'success');
            }
            setEditing(null);
            await refetch();
        } catch (err: any) {
            notify(err?.message || 'Failed to save the task', 'error');
        }
    };

    const handleComplete = async (task: CrmTask) => {
        try {
            await handleCompleteTask({ variables: { taskId: task._id } });
            notify(`Task '${task.title}' completed`, 'success');
            await refetch();
        } catch (err: any) {
            notify(err?.message || 'Failed to complete the task', 'error');
        }
    };

    return (
        <MainCard
            title="Tasks"
            contentSX={{ p: 0, '&:last-child': { pb: 0 } }}
            secondary={
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
                    Add task
                </Button>
            }
        >
            {loading && !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No open tasks
                </Typography>
            ) : (
                <List disablePadding>
                    {tasks.map((task) => (
                        <ListItem key={task._id} divider disablePadding sx={{ pl: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Checkbox
                                    disableRipple
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => handleComplete(task)}
                                    inputProps={{ 'aria-label': `Complete task ${task.title}` }}
                                />
                            </ListItemIcon>
                            <ListItemButton onClick={() => setEditing(task)}>
                                <ListItemText
                                    primary={task.title}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={
                                        <>
                                            {dueCell(task)}
                                            {task.accountId && (
                                                <Link
                                                    component={RouterLink}
                                                    to={`/crm/accounts/${task.accountId}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    display="block"
                                                >
                                                    {task.accountName || 'Linked business'}
                                                </Link>
                                            )}
                                            {task.contactId && (
                                                <Link
                                                    component={RouterLink}
                                                    to={`/crm/contacts/${task.contactId}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    display="block"
                                                >
                                                    {task.contactName || 'Linked contact'}
                                                </Link>
                                            )}
                                        </>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}

            <TaskDialog
                open={!!editing}
                task={editing === 'new' ? null : editing}
                admins={admins}
                saving={creating || updating}
                onClose={() => setEditing(null)}
                onSave={handleSave}
            />
        </MainCard>
    );
};

export default TasksPanel;
